/**
 * Rapier wrapper — the collision and query engine. (§1.2)
 *
 * ── The central design decision, stated once so nobody has to re-derive it ──
 *
 * Rapier does NOT own the simulation state. WorldState does.
 *
 * The obvious approach — make the ball a dynamic rigid body, let Rapier
 * integrate it, and read the transform out for rendering — puts the
 * authoritative state inside the WASM heap. That breaks two things the netcode
 * cannot live without:
 *
 *   1. Reconciliation (§2.4) has to rewind to an arbitrary past state and
 *      re-simulate N ticks, dozens of times a second. With state inside Rapier
 *      that means world.takeSnapshot()/restoreSnapshot() per rewind, which
 *      serializes the ENTIRE world to a byte array. It is designed for save
 *      games, not for a 60Hz rollback path.
 *
 *   2. Cloning for the prediction ring buffer (§2.3) has the same problem, ×64.
 *
 * So: we keep POD state in WorldState, push it into Rapier each tick, step,
 * and read the results back. Rapier answers "what did I hit and how should I
 * respond", not "where is everything". Clone and rewind stay buffer copies.
 *
 * ── What this costs, honestly ───────────────────────────────────────────────
 *
 * Writing a rigid body's transform+velocity each tick discards Rapier's
 * internal solver warm-start and contact caches. For stacked boxes that would
 * ruin stability. For our world — one sphere, 22 kinematic capsules, and static
 * geometry — there is nothing to stack and nothing to jitter, so we lose
 * essentially nothing. This is the standard pattern for rollback netcode.
 *
 * It does mean a restored state is not bit-identical in Rapier's *internal*
 * caches, only in the state we own. That is fine and worth being precise about:
 *
 *   - §13.1's determinism test runs two fresh sims from tick 0 with no restore.
 *     Same WASM, same inputs, same POD state → bit-identical. The test is valid.
 *   - Reconciliation restores and re-simulates. The result converges to the
 *     server rather than matching it bit-for-bit. That is exactly what
 *     POSITION_EPSILON (§2.4) exists to absorb, and it is why the model is
 *     "server-authoritative + reconciliation" and not lockstep (§2.1).
 *
 * If someone later reports "the determinism test is green but clients still
 * drift under packet loss", this paragraph is the answer: they are different
 * guarantees, and only the first one is bit-exact by design.
 */

import type { BallState, MatchConfig, WorldState } from './types.js';

// ---------------------------------------------------------------------------
// Collision groups — Rapier interaction groups are (membership << 16) | filter.
// ---------------------------------------------------------------------------

export const enum CollisionGroup {
  Ball = 1 << 0,
  Player = 1 << 1,
  Ground = 1 << 2,
  Goal = 1 << 3, // posts + crossbar
  GoalNet = 1 << 4,
  GoalSensor = 1 << 5, // the "ball fully crossed" trigger volume
  Boundary = 1 << 6, // out-of-play sensors
}

export function interactionGroups(membership: number, filter: number): number {
  return ((membership & 0xffff) << 16) | (filter & 0xffff);
}

// ---------------------------------------------------------------------------
// Contact reporting
// ---------------------------------------------------------------------------

export const enum ContactKind {
  None = 0,
  Ground = 1,
  Post = 2,
  Crossbar = 3,
  Net = 4,
  Player = 5,
  Boundary = 6,
  /**
   * The world-edge containment walls (the §2.8 encodable-volume boundary), as
   * distinct from the flat pitch slab. The distinction matters because the slab's
   * true surface normal is exactly (0,1,0) — resolveSurface relies on that to
   * discard the degenerate normals Rapier reports for a ball resting flush on it —
   * while a wall's real (horizontal) normal must be respected. Internal physics
   * tag only; never serialized.
   */
  Wall = 7,
}

/**
 * A ball contact for this tick. Pooled and reused — never allocated in the loop.
 * The ball can legitimately hit more than one thing per tick (post then net),
 * so this is an array, not a single value.
 */
export interface BallContact {
  kind: ContactKind;
  /** PlayerId for ContactKind.Player, else -1. */
  playerId: number;
  /** Contact normal, pointing away from the surface. */
  nx: number;
  ny: number;
  nz: number;
  /** Contact point, world space. */
  px: number;
  py: number;
  pz: number;
  /** Penetration depth (positive = overlapping). */
  depth: number;
}

export interface PhysicsQueryResult {
  hit: boolean;
  playerId: number;
  /** ContactKind of the surface hit (Ground/Wall/Post/Crossbar/Net/Player). */
  kind: ContactKind;
  toi: number;
  nx: number; ny: number; nz: number;
  px: number; py: number; pz: number;
}

// ---------------------------------------------------------------------------
// The wrapper
// ---------------------------------------------------------------------------

/**
 * The physics interface the sim codes against.
 *
 * Deliberately narrow: the sim asks about contacts and sweeps, and nothing
 * else. Keeping Rapier behind this interface means the ball model (§3.2) and
 * the player controller (§3.3) never import Rapier directly, so they stay
 * unit-testable without loading WASM — which matters, because those two files
 * are where all the feel lives and they will be iterated on hundreds of times.
 */
export interface PhysicsWorld {
  /** Rebuild static geometry for a new pitch size. (§9.2) */
  configurePitch(config: MatchConfig): void;

  /**
   * Push POD state → Rapier, step the collision world, read contacts back.
   * Called once per tick from stepBall.
   */
  syncAndStep(state: WorldState): void;

  /** Ball contacts detected during the last syncAndStep. Valid until next call. */
  getBallContacts(): ReadonlyArray<BallContact>;

  /**
   * Continuous sweep of the ball sphere from its current position along `d`.
   * Mandatory for the ball: at 30 m/s it moves 0.5m per tick and will tunnel
   * through the net or a foot without this (§3.4).
   *
   * `excludePlayerId` (optional): exclude that player's capsule from the cast.
   * Used for the ball's CARRIER — a dribbler's body must not wall off the ball
   * he is shepherding (the touch model is that interaction); without this a
   * carried ball flush with the capsule loses displacement to spurious sweep
   * hits and gets swallowed on sprint carries. Opponents still block normally.
   */
  sweepBall(
    fromX: number, fromY: number, fromZ: number,
    dx: number, dy: number, dz: number,
    out: PhysicsQueryResult,
    excludePlayerId?: number,
  ): void;

  /**
   * Move a kinematic character capsule with collide-and-slide.
   * Players are kinematic, NOT dynamic rigid bodies — we need direct control
   * for responsiveness (§3.3).
   */
  moveCharacter(
    playerId: number,
    dx: number, dy: number, dz: number,
    out: { x: number; y: number; z: number; grounded: boolean },
  ): void;

  /**
   * Capsule sweep for GK dives and tackle reach checks.
   * Used by lag compensation against rewound hitboxes (§2.6).
   */
  sweepCapsule(
    fromX: number, fromY: number, fromZ: number,
    dx: number, dy: number, dz: number,
    radius: number, halfHeight: number,
    excludePlayerId: number,
    out: PhysicsQueryResult,
  ): void;

  /** Free the WASM world. Call on match end — Fly Machines are per-match (§12.2). */
  dispose(): void;
}

// ---------------------------------------------------------------------------
// Pitch geometry derived from config
// ---------------------------------------------------------------------------

/**
 * Pitch extents. Origin is the centre spot; +X is toward team A's target goal
 * when teamAAttacksPositiveX. Y is up. Z is across the pitch.
 */
export interface PitchBounds {
  halfLength: number;
  halfWidth: number;
  goalLineX: number;
  touchlineZ: number;
}

export function pitchBounds(config: MatchConfig): PitchBounds {
  const halfLength = config.pitchLength * 0.5;
  const halfWidth = config.pitchWidth * 0.5;
  return {
    halfLength,
    halfWidth,
    goalLineX: halfLength,
    touchlineZ: halfWidth,
  };
}

/**
 * Whether the WHOLE ball has crossed the WHOLE line. (§5.2)
 *
 * This is the actual Law 9 test and it is not the obvious one: a ball resting
 * ON the line is still IN play. The ball is out only when its trailing edge has
 * passed the line's outer edge — hence `- radius` here rather than a naive
 * centre-vs-line compare, which would call the ball out ~11cm early, every time,
 * on every touchline. Players notice this immediately.
 */
export function ballFullyCrossedLine(
  ballCoord: number,
  ballRadius: number,
  lineCoord: number,
  lineIsPositive: boolean,
): boolean {
  return lineIsPositive
    ? ballCoord - ballRadius > lineCoord
    : ballCoord + ballRadius < lineCoord;
}

/** Ball height is part of the test — a ball can cross the line in the air. (§5.2) */
export function ballCrossedGoalLine(ball: BallState, bounds: PitchBounds, radius: number): 0 | 1 | -1 {
  if (ballFullyCrossedLine(ball.px, radius, bounds.goalLineX, true)) return 1;
  if (ballFullyCrossedLine(ball.px, radius, -bounds.goalLineX, false)) return -1;
  return 0;
}
